import React, { useMemo, useState } from "react";
import {
  Box,
  Card,
  CardBody,
  Flex,
  HStack,
  SimpleGrid,
  Tag,
  Text,
  useToast,
  Wrap,
  WrapItem,
} from "@chakra-ui/react";
import { CalendarBlank, ChartLine, Factory, Leaf, Lightning, User } from "@phosphor-icons/react";
import { useNavigate } from "react-router-dom";
import FilterPill from "../FilterPill";
import { constructQueryParamsString, SUPPLIER_QUESTIONNAIRE_TYPES, showErrorToast } from "../../../../utils";
import { useGetAllSupplierQuestionnaires } from "../../../../hooks/query/supplier-questionnaire";
import LocalSpinner from "../../../../components/LocalSpinner";
import { REPORTING_YEARS } from "../../suppliersMock";
import { routes } from "../../../../config/paths";

const YEAR_OPTIONS = REPORTING_YEARS.map((y) => ({ value: String(y), label: String(y) }));

const TYPE_COLORS = {
  [SUPPLIER_QUESTIONNAIRE_TYPES.EMISSION_BASED]:            { light: "#E8FAF0", text: "#0B5E30", icon: Factory,    label: "Emission Based" },
  [SUPPLIER_QUESTIONNAIRE_TYPES.CONSUMPTION_BASED]:         { light: "#E8F4FD", text: "#1A5276", icon: Lightning,  label: "Consumption Based" },
  [SUPPLIER_QUESTIONNAIRE_TYPES.DECARBONIZATION_NET_ZERO_PLAN]: { light: "#F3E8FE", text: "#5B1D99", icon: Leaf,   label: "Decarbonisation Plan" },
};

const TYPE_OPTIONS = Object.entries(TYPE_COLORS).map(([value, { label }]) => ({ value, label }));

const STATUS_CONFIG = {
  NEW:         { progress: 15,  color: "#FC8181", label: "No data" },
  IN_PROGRESS: { progress: 50,  color: "#FBD38D", label: "In Progress" },
  FINALIZED:   { progress: 100, color: "#B8E086", label: "Finalised" },
};

const STATUS_OPTIONS = [
  { value: "NEW",         label: "No data" },
  { value: "IN_PROGRESS", label: "In Progress" },
  { value: "FINALIZED",   label: "Finalised" },
];

const getTitle = (task) => {
  if (task.description) return task.description;
  const parts = [];
  if (task.period?.year) parts.push(task.period.year);
  if (task.period?.quarter && task.period.quarter !== "ALL") parts.push(task.period.quarter);
  return parts.join(" · ") || "—";
};

const TaskCard = ({ task, onClick }) => {
  const typeConfig = TYPE_COLORS[task.questionnaireType];
  const statusConfig = STATUS_CONFIG[task.ghgStatus] ?? STATUS_CONFIG.NEW;
  const title = getTitle(task);
  const assignee = task.assigneeName || task.inviteeEmail || null;

  return (
    <Card
      w="100%"
      borderWidth="1px"
      borderRadius="lg"
      borderColor="gray.200"
      bg="white"
      boxShadow="sm"
      cursor="pointer"
      _hover={{ borderColor: "lime" }}
      transition="border-color 0.15s"
      onClick={onClick}
    >
      {/* Header strip with type badge */}
      <Flex
        justify="space-between"
        align="center"
        bg="gray.50"
        px={5}
        py={3}
        borderBottom="1px solid"
        borderColor="gray.100"
        borderTopRadius="lg"
        gap={3}
      >
        <Wrap spacing={1.5} flex={1} minW={0}>
          {typeConfig ? (
            <WrapItem>
              <Box
                display="inline-flex"
                alignItems="center"
                gap="4px"
                bg={typeConfig.light}
                color={typeConfig.text}
                fontSize="11px"
                fontWeight="600"
                px="8px"
                h="20px"
                borderRadius="full"
                flexShrink={0}
                whiteSpace="nowrap"
              >
                <typeConfig.icon size={12} weight="fill" style={{ flexShrink: 0 }} />
                <Box as="span" whiteSpace="nowrap">{typeConfig.label}</Box>
              </Box>
            </WrapItem>
          ) : (
            <WrapItem>
              <Box
                display="inline-flex"
                alignItems="center"
                gap="4px"
                bg="gray.100"
                color="gray.600"
                fontSize="11px"
                fontWeight="600"
                px="8px"
                h="20px"
                borderRadius="full"
              >
                <ChartLine size={12} weight="fill" />
                <Box as="span">{task.questionnaireType ?? "Task"}</Box>
              </Box>
            </WrapItem>
          )}
        </Wrap>
      </Flex>

      <CardBody px={5} py={4} display="flex" flexDirection="column">
        {/* Title */}
        <Text fontSize="15px" fontWeight="600" color="gray.800" lineHeight="1.4" noOfLines={2} mb={2}>
          {title}
        </Text>

        {/* Period + assignee */}
        <HStack spacing={3} mb={3} flexWrap="wrap" rowGap={1}>
          {task.period?.year && (
            <HStack spacing={1.5}>
              <CalendarBlank size={13} color="#A0AEC0" />
              <Text fontSize="13px" color="gray.500">
                {task.period.year}{task.period.quarter ? ` ${task.period.quarter}` : ""}
              </Text>
            </HStack>
          )}
          {assignee && (
            <Tag size="sm" borderRadius="md" bg="#FFF8E1" px={2} py={0.5} gap={1.5}>
              <User size={12} color="#718096" />
              <Text fontSize="11px" fontWeight="600" color="gray.700" textTransform="uppercase">
                {assignee}
              </Text>
            </Tag>
          )}
        </HStack>

        {/* Status progress bar */}
        <Box mt="auto" pt={3} maxW="50%" minW="120px">
          <Flex alignItems="center" gap={1.5} mb={1}>
            <Text fontSize="xs" fontWeight="600" color="gray.600">{statusConfig.label}</Text>
          </Flex>
          <Box h="10px" borderRadius="full" bg="gray.100" overflow="hidden">
            <Box h="100%" w={`${statusConfig.progress}%`} borderRadius="full" bg={statusConfig.color} />
          </Box>
        </Box>
      </CardBody>
    </Card>
  );
};

// Data tasks for a supplier — pulled from GET /supplier-questionnaires?supplier=<name>
function SupplierTasks({ supplier }) {
  const toast = useToast();
  const navigate = useNavigate();

  const [yearFilter, setYearFilter] = useState([]);
  const [typeFilter, setTypeFilter] = useState([]);
  const [statusFilter, setStatusFilter] = useState([]);

  const queryParams = useMemo(
    () => constructQueryParamsString({ supplier: supplier.name, page: 0, size: 200 }),
    [supplier.name]
  );

  const { data, isFetching } = useGetAllSupplierQuestionnaires(queryParams, {
    onError: (err) => showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message),
    cacheTime: 0,
  });

  // SupplierQuestionnaire renders a finalized task read-only via its own
  // isPreview logic, so the same route works for both editable and
  // finalized tasks — no separate modal needed. Pass `from` so its
  // "Back to tasks" button returns here rather than the generic
  // Supplier Questionnaires / Data entry planning pages.
  const handleCardClick = (task) =>
    navigate(`${routes.supplierQuestionnaire}?id=${task.id}`, {
      state: {
        from: routes.supplyChainSupplierDetail.replace(":supplierId", supplier.id),
        fromState: { supplier },
      },
    });

  const allTasks = useMemo(() => (data?.content ?? []).sort((a, b) => {
    const yearDiff = (b.period?.year ?? 0) - (a.period?.year ?? 0);
    if (yearDiff !== 0) return yearDiff;
    return (a.assigneeName ?? "").localeCompare(b.assigneeName ?? "");
  }), [data]);

  const filtered = useMemo(() => {
    const years = new Set(yearFilter.map((y) => y.value));
    const types = new Set(typeFilter.map((t) => t.value));
    const statuses = new Set(statusFilter.map((s) => s.value));
    return allTasks.filter((t) => {
      if (years.size && !years.has(String(t.period?.year))) return false;
      if (types.size && !types.has(t.questionnaireType)) return false;
      if (statuses.size && !statuses.has(t.ghgStatus)) return false;
      return true;
    });
  }, [allTasks, yearFilter, typeFilter, statusFilter]);

  return (
    <Box>
      <Flex justify="space-between" align="center" mb={4} gap={3} flexWrap="wrap">
        <Text fontSize="22px" fontWeight="700" color="darkTeal">Data tasks</Text>
        <HStack spacing={2} flexWrap="nowrap">
          <FilterPill label="Year" options={YEAR_OPTIONS} selected={yearFilter} onChange={setYearFilter} />
          <FilterPill label="Type" options={TYPE_OPTIONS} selected={typeFilter} onChange={setTypeFilter} />
          <FilterPill label="Status" options={STATUS_OPTIONS} selected={statusFilter} onChange={setStatusFilter} />
        </HStack>
      </Flex>

      {isFetching ? (
        <Box mt={4}><LocalSpinner /></Box>
      ) : allTasks.length === 0 ? (
        <Box border="1px dashed" borderColor="gray.200" borderRadius="md" py={10} textAlign="center">
          <Text color="gray.400" fontSize="14px">No data tasks assigned for this supplier.</Text>
        </Box>
      ) : filtered.length === 0 ? (
        <Box border="1px dashed" borderColor="gray.200" borderRadius="md" py={10} textAlign="center">
          <Text color="gray.400" fontSize="14px">No tasks match your filters.</Text>
        </Box>
      ) : (
        <SimpleGrid columns={{ base: 1, md: 2, xl: 3 }} spacing={4}>
          {filtered.map((task) => (
            <TaskCard key={task.id} task={task} onClick={() => handleCardClick(task)} />
          ))}
        </SimpleGrid>
      )}
    </Box>
  );
}

export default SupplierTasks;
